import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router'
import { ErrorState } from '@/components/ui/states'
import { useSession } from '@/features/auth/api'
import { pendingInvitation, safeNext } from '@/features/auth/auth-utils'
import type { InvitationPreview } from '@/lib/api/types'
import { portalMode } from '@/lib/portal'
import { useSwitchWorkspace } from '@/features/workspaces/api'
import { FullPageLoader, NotFoundPage } from './fallbacks'
import { usePortalSite } from '@/features/portal/portal-site'

/** Session-gated area. The API is the real gate; this only avoids rendering a shell for guests. */
export function RequireAuth() {
  const session = useSession()
  const location = useLocation()

  if (session.isPending) return <FullPageLoader />
  if (session.isError)
    return (
      <ErrorState
        error={session.error}
        onRetry={() => void session.refetch()}
        className="mx-auto max-w-md py-24"
      />
    )
  if (!session.data) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }
  return <Outlet />
}

export function GuestOnly() {
  const session = useSession()
  const location = useLocation()
  const queryClient = useQueryClient()
  if (session.isPending) return <FullPageLoader />
  if (session.data) {
    // Auth mutations update the shared session before a page's awaited submit handler resumes.
    // This guard can therefore navigate first, and must preserve the same invitation/return intent.
    const token = pendingInvitation.get()
    const invitation = token
      ? queryClient.getQueryData<InvitationPreview>(['invitation-preview', token])
      : undefined
    // Registration already waits for this preview. Legacy and portal signup accept on the server;
    // sending them back to review here would race the submit handler clearing their old token.
    const acceptedBySignup =
      location.pathname === '/register' &&
      (portalMode || (invitation?.status === 'pending' && invitation.requires_explicit_acceptance !== true))
    const next = safeNext(new URLSearchParams(location.search).get('next'))
    const destination =
      token && !acceptedBySignup
        ? '/accept-invitation'
        : acceptedBySignup ||
            (location.pathname === '/register' && session.data.user.assistant_onboarding_pending) ||
            next.split(/[?#]/)[0] === location.pathname
          ? '/'
          : next
    return <Navigate to={destination} replace />
  }
  return <Outlet />
}

/** "/" sends people to their default workspace, or to create one if they have none. */
export function RootRedirect() {
  const session = useSession()
  const portal = usePortalSite()
  if (session.isPending) return <FullPageLoader />
  if (!session.data) return <Navigate to="/login" replace />

  // On an agency's portal address, its workspace comes first.
  const { workspaces, current_workspace_id } = session.data
  const target =
    workspaces.find((w) => w.id === portal?.workspace_id) ??
    workspaces.find((w) => w.id === current_workspace_id) ??
    workspaces[0]
  // First-time setup is for a new customer in their own workspace: an invited teammate who
  // signed up carries no such setup into the team's workspace.
  const onboarding = session.data.user.assistant_onboarding_pending && !portalMode && target?.role === 'owner'
  return (
    <Navigate
      to={target ? `/w/${target.id}${onboarding ? '/assistant?onboarding=1' : ''}` : '/workspaces/new'}
      replace
    />
  )
}

/**
 * "/lifetime" (arvaseo.com/lifetime/'s buy buttons): Plan & billing's lifetime plans in a workspace
 * the person owns (only owners buy), else their current workspace.
 */
export function LifetimeRedirect() {
  const session = useSession()
  if (session.isPending) return <FullPageLoader />
  if (!session.data) return <Navigate to="/login?next=%2Flifetime" replace />
  const { workspaces, current_workspace_id } = session.data
  const owned = workspaces.filter((w) => w.role === 'owner')
  const target =
    owned.find((w) => w.id === current_workspace_id) ??
    owned[0] ??
    workspaces.find((w) => w.id === current_workspace_id) ??
    workspaces[0]
  return <Navigate to={target ? `/w/${target.id}/settings/plan?lifetime=1` : '/workspaces/new'} replace />
}

/** Renders the workspace area only for workspaces in the user's session. */
export function WorkspaceGate() {
  const { workspaceId } = useParams()
  const location = useLocation()
  const session = useSession()
  const switchWorkspace = useSwitchWorkspace()
  const workspace = session.data?.workspaces.find((w) => w.id === workspaceId)
  const { mutate } = switchWorkspace

  // Remember the last workspace visited so "/" returns here next time.
  useEffect(() => {
    if (workspace && session.data?.current_workspace_id !== workspace.id) mutate(workspace.id)
  }, [workspace, session.data?.current_workspace_id, mutate])

  if (!workspace) return <NotFoundPage />
  // A client login only has its portal.
  if (workspace.role === 'client' && !location.pathname.startsWith(`/w/${workspace.id}/portal`)) {
    return <Navigate to={`/w/${workspace.id}/portal`} replace />
  }
  return <Outlet />
}
